import { useState } from "react";
import { useI18n } from "../contexts/LanguageContext";
import api from "../lib/api";
import { toast } from "sonner";
import { MapPin, Phone, Mail, MessageCircle } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import useSeo from "../hooks/useSeo";

const TEAM = [
  { name: "Enzo Medina", role: "Account Manager", phone: "+1 (201) 470-9244", email: "enzo@encesglobal.info", wa: "12014709244" },
  { name: "Enrique Bravo", role: "Account Manager", phone: "+1 (551) 697-5955", email: "ebravo@encesglobal.info", wa: "15516975955" },
  { name: "Juan Carlos", role: "Recruiting Lead", phone: "+1 (551) 233-7689", email: "juancarlos@encesglobal.info", wa: "15512337689" },
];

export default function Contact() {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useSeo({
    title: "Contact ENCES Global",
    description: "Get in touch with ENCES Global - bilingual staffing agency in Union City, NJ. Phone: +1 (201) 470-9244. Email: enzo@encesglobal.info. 505 48th St. Suite 2, Union City, NJ 07087.",
    path: "/contact",
  });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", { ...form, type: "general" });
      toast.success(t.contact.success);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch { toast.error(t.contact.error); } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="page-contact">
      <section className="bg-navy text-white">
        <div className="container-pad mx-auto max-w-[1400px] py-20">
          <div className="text-xs tracking-[0.3em] uppercase text-white/60 mb-3">{t.nav.contact}</div>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight" data-testid="contact-title">{t.contact.title}</h1>
          <p className="mt-3 text-white/70 text-lg max-w-2xl">{t.contact.subtitle}</p>
        </div>
      </section>

      <section className="container-pad mx-auto max-w-[1400px] py-16 grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* LEFT: office + team */}
        <div className="space-y-6">
          <div className="bg-white border border-slate-100 rounded-[24px] p-7 shadow-soft">
            <div className="text-xs uppercase tracking-widest text-brandred mb-2">{t.contact.office}</div>
            <div className="flex items-start gap-3">
              <MapPin className="text-navy mt-1" />
              <div className="text-navy font-bold">505 48th St. Suite 2<br /><span className="font-semibold text-slate-600">Union City, NJ 07087</span></div>
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest text-brandred mb-3">{t.contact.team}</div>
            <div className="space-y-4">
              {TEAM.map((m, i) => (
                <div key={m.name} className="bg-white border border-slate-100 rounded-[24px] p-6 flex gap-5 hover:shadow-xl transition-shadow" data-testid={`team-card-${i}`}>
                  <div className="w-14 h-14 rounded-2xl bg-navy text-white inline-flex items-center justify-center font-extrabold text-xl shrink-0">{m.name[0]}</div>
                  <div className="flex-1">
                    <div className="font-bold text-navy text-lg">{m.name}</div>
                    <div className="text-sm text-slate-500">{m.role}</div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <a href={`tel:${m.phone.replace(/\D/g, "")}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-soft text-navy text-xs font-semibold hover:bg-slate-200" data-testid={`team-phone-${i}`}><Phone size={12} /> {m.phone}</a>
                      <a href={`https://wa.me/${m.wa}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-whatsapp/10 text-whatsapp text-xs font-semibold hover:bg-whatsapp hover:text-white transition-colors" data-testid={`team-wa-${i}`}><MessageCircle size={12} /> WhatsApp</a>
                      <a href={`mailto:${m.email}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-brandred/10 text-brandred text-xs font-semibold hover:bg-brandred hover:text-white transition-colors" data-testid={`team-email-${i}`}><Mail size={12} /> {m.email}</a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT: form */}
        <form onSubmit={submit} className="bg-white border border-slate-100 rounded-[24px] p-7 md:p-9 shadow-soft self-start space-y-5" data-testid="contact-form">
          <div>
            <Label htmlFor="c-name">{t.contact.name} *</Label>
            <Input id="c-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 rounded-xl" data-testid="contact-name" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="c-email">{t.contact.email} *</Label>
              <Input id="c-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 rounded-xl" data-testid="contact-email" />
            </div>
            <div>
              <Label htmlFor="c-phone">{t.contact.phone}</Label>
              <Input id="c-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 rounded-xl" data-testid="contact-phone" />
            </div>
          </div>
          <div>
            <Label htmlFor="c-message">{t.contact.message} *</Label>
            <Textarea id="c-message" required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5 rounded-xl" data-testid="contact-message" />
          </div>
          <button type="submit" disabled={submitting} className="w-full px-6 py-4 rounded-full bg-brandred hover:bg-red-700 text-white font-semibold shadow-lg shadow-red-500/20 disabled:opacity-60" data-testid="contact-submit">
            {submitting ? t.common.loading : t.contact.send}
          </button>
        </form>
      </section>
    </div>
  );
}
